'use client';

import { useMemo } from 'react';
import { useQueries } from '@zg-rentals/react-query5';

import type { SearchListingsRequest } from '@/src/lib/api';
import { applyFilterRemoval, type FilterPill, type FilterState, getReliefFilterPills } from '@/src/lib/filters';
import { searchUnitCountQueryOptions } from '@/src/lib/hooks/query';
import { buildFilterProbeRequest, buildUnfilteredCountRequest } from '../_data/searchRequest';

// One request each, so cap the fan-out. Chips past the cap stay removable, just
// without a count.
export const MAX_PROBED_FILTERS = 8;

export type ExcludedListingCount = {
  pill: FilterPill;
  /**
   * Listings that would match if this one selection were removed. `null` when not
   * known — in flight, failed, or past {@link MAX_PROBED_FILTERS} — which callers
   * must distinguish from a resolved 0.
   */
  count: number | null;
};

export type ExcludedListingCounts = {
  /** Listings in the same viewport with every filter cleared; null until known. */
  unfilteredTotal: number | null;
  /** One entry per active filter, in pill order. Empty when there is no relief to offer. */
  chips: Array<ExcludedListingCount>;
};

/**
 * How many listings the viewport holds unfiltered, and how many each active
 * filter is keeping out. Each figure is its own `/search/listings` call, so this
 * only belongs on a search that returned nothing.
 */
export function useExcludedListingCounts({
  request,
  filters,
  pills,
  initialUnfilteredTotal,
}: {
  /** The live SRP search request — supplies the viewport and area clip to probe within. */
  request: SearchListingsRequest;
  filters: FilterState;
  pills: Array<FilterPill>;
  /** Must describe `request`; pass null once the live request has moved off the SSR one. */
  initialUnfilteredTotal?: number | null;
}): ExcludedListingCounts {
  const { probed, unprobed } = useMemo(() => {
    const relief = getReliefFilterPills(pills);
    return { probed: relief.slice(0, MAX_PROBED_FILTERS), unprobed: relief.slice(MAX_PROBED_FILTERS) };
  }, [pills]);

  // Slot 0 is the baseline, then one per probed chip. With a single active filter,
  // removing it IS the unfiltered search, so they share a slot rather than handing
  // `useQueries` a duplicate key.
  const { requests, slots } = useMemo(() => {
    const all = [
      buildUnfilteredCountRequest(request),
      ...probed.map((pill) => buildFilterProbeRequest(request, applyFilterRemoval(filters, pill.key, pill.value))),
    ];
    const slotByRequest = new Map<string, number>();
    const unique: Array<SearchListingsRequest> = [];
    const assigned = all.map((probeRequest) => {
      const identity = JSON.stringify(probeRequest);
      const existing = slotByRequest.get(identity);
      if (existing !== undefined) return existing;
      const slot = unique.push(probeRequest) - 1;
      slotByRequest.set(identity, slot);
      return slot;
    });
    return { requests: unique, slots: assigned };
  }, [request, filters, probed]);

  const results = useQueries({
    queries: requests.map((probeRequest, slot) => {
      const options = searchUnitCountQueryOptions(probeRequest);
      return {
        ...options,
        enabled: options.enabled && probed.length > 0,
        initialData: slot === slots[0] ? (initialUnfilteredTotal ?? undefined) : undefined,
      };
    }),
  });

  const countAt = (index: number): number | null => results[slots[index]]?.data ?? null;

  return {
    unfilteredTotal: countAt(0),
    chips: [
      ...probed.map((pill, index) => ({ pill, count: countAt(index + 1) })),
      ...unprobed.map((pill) => ({ pill, count: null })),
    ],
  };
}
